import React from 'react'
import { Link } from 'react-router-dom'
import { FaArrowRight, FaSeedling, FaCogs, FaPaw, FaFish, FaQuoteLeft } from 'react-icons/fa'
import Hero from '../components/Hero'
import KeyNumbers from '../components/KeyNumbers'
import ImageWithFallback from '../components/ImageWithFallback'
import { activities, testimonials, news } from '../data/siteData'

const Home = () => {
  return (
    <div className="Home">
      {/* Hero Section */}
      <Hero />

      {/* Key Numbers Section */}
      <KeyNumbers />

      {/* About Preview Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 via-white to-primary-50/30 overflow-hidden relative">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-0 left-0 w-72 h-72 bg-primary-100/50 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent-100/40 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.8)_0%,transparent_70%)]"></div>
        </div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-right" className="space-y-8">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-500/10 to-accent-500/10 rounded-full mb-6">
                  <span className="text-primary-600 font-semibold text-sm">Notre Histoire</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                  Qui sommes-nous ?
                  <div className="h-1.5 w-24 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full mt-6"></div>
                </h2>
              </div>
              
              <div className="space-y-6 text-lg text-gray-600">
                <p className="leading-relaxed">
                  La <span className="font-semibold text-primary-600">SCOOP - CG 1 GEA R.SL</span> est une coopérative agricole dynamique de la région de Saint-Louis, 
                  rassemblant plus de <span className="font-semibold text-accent-600">350 membres</span> déterminés à développer une agriculture durable et prospère.
                </p>
                <p className="leading-relaxed">
                  Depuis <span className="font-semibold text-secondary-600">15 ans</span>, nous œuvrons pour la valorisation des productions locales à travers 
                  la production, la transformation, l'élevage et l'aquaculture, en privilégiant des pratiques 
                  respectueuses de l'environnement.
                </p>
              </div>

              {/* Key features */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-bold text-xl">350</span>
                    </div>
                    <div>
                      <span className="text-gray-900 font-semibold">Membres actifs</span>
                      <div className="h-1 w-12 bg-primary-500/30 rounded-full mt-1"></div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center">
                      <span className="text-accent-600 font-bold text-xl">15</span>
                    </div>
                    <div>
                      <span className="text-gray-900 font-semibold">Ans d'expérience</span>
                      <div className="h-1 w-12 bg-accent-500/30 rounded-full mt-1"></div>
                    </div>
                  </div>
                </div>
              </div>

              <Link 
                to="/qui-sommes-nous" 
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
              >
                <span>Découvrir notre histoire</span>
                <FaArrowRight className="ml-3 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            <div data-aos="fade-left" className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <ImageWithFallback
                  src="https://saed.sn/static/assets/images/services/service-2-5.png"
                  alt="Agriculture familiale appuyée par la SAED"
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Info Card */}
                <div className="absolute -bottom-6 -right-6 bg-white p-8 rounded-2xl shadow-xl backdrop-blur-sm">
                  <div className="text-center">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Partenariat SAED
                    </h4>
                    <p className="text-gray-600 text-sm mb-4">
                      Techniques modernes
                    </p>
                    <div className="flex space-x-4 text-sm">
                      <div>
                        <span className="font-bold text-primary-600">350+</span>
                        <br />
                        <span className="text-gray-500">Membres</span>
                      </div>
                      <div>
                        <span className="font-bold text-accent-600">25</span>
                        <br />
                        <span className="text-gray-500">Projets</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-primary-500/10 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl"></div>
              
              {/* Pattern overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="w-full h-full" style={{
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23000000\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                  opacity: 0.1
                }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities Preview Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 to-white pointer-events-none"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23000000\' fill-opacity=\'0.03\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.5
        }}></div>
        
        <div className="container mx-auto max-w-7xl px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-500/10 to-accent-500/10 rounded-full mb-6">
              <span className="text-primary-600 font-semibold text-sm">🌾 Nos Expertises</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Nos activités
              <div className="h-1.5 w-24 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full mx-auto mt-6"></div>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Découvrez nos quatre domaines d'expertise qui font la richesse et la diversité 
              de notre coopérative agricole. Chaque activité reflète notre engagement pour l'excellence et la durabilité.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {activities.map((activity, index) => {
              const icons = {
                'Production': FaSeedling,
                'Transformation': FaCogs,
                'Élevage': FaPaw,
                'Aquaculture': FaFish
              }
              const Icon = icons[activity.title] || FaSeedling

              return (
                <div
                  key={activity.id}
                  className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  {/* Image Container */}
                  <div className="relative h-64 overflow-hidden">
                    <ImageWithFallback
                      src={activity.image}
                      alt={activity.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Icon Badge */}
                    <div className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg transform -translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                      <Icon className="text-primary-500 text-xl" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">
                      {activity.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                      {activity.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {activity.features.slice(0, 3).map((feature, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full font-medium group-hover:bg-primary-100 transition-colors duration-300"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                </div>
              )
            })}
          </div>

          <div className="text-center mt-16">
            <Link 
              to="/nos-activites" 
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
            >
              <span>Voir toutes nos activités</span>
              <FaArrowRight className="ml-3 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-accent-50 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-primary-200/30 to-accent-200/30 rounded-full blur-2xl"></div>
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-tr from-secondary-200/30 to-primary-200/30 rounded-full blur-2xl"></div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full mb-6 mx-auto">
              <FaQuoteLeft className="text-2xl text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Témoignages
              <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full mx-auto mt-4"></div>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Découvrez les histoires inspirantes de nos membres et leur parcours de réussite 
              grâce à l'accompagnement de notre coopérative
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl relative overflow-hidden group transition-all duration-300 hover:-translate-y-1"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                {/* Decorative background */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-50 rounded-full opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                
                <FaQuoteLeft className="text-3xl text-primary-400 mb-6 relative z-10" />
                <p className="text-gray-700 mb-6 italic leading-relaxed text-lg relative z-10">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center relative z-10">
                  <div className="relative">
                    <ImageWithFallback
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover border-3 border-white shadow-lg"
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-white"></div>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900 text-lg">{testimonial.name}</h4>
                    <p className="text-primary-600 text-sm font-medium">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="section-padding bg-white relative">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-500 via-accent-500 to-secondary-500"></div>
        
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-16 gap-6">
            <div className="flex-1">
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-gradient-to-r from-secondary-500 to-primary-500 text-white text-sm font-semibold rounded-full">
                  📰 Actualités
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                Dernières nouvelles
                <div className="w-20 h-1 bg-gradient-to-r from-secondary-500 to-primary-500 rounded-full mt-3"></div>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                Restez informés de nos dernières activités, événements et réalisations 
                qui marquent notre engagement quotidien
              </p>
            </div>
            <Link 
              to="/actualites" 
              className="group bg-gradient-to-r from-secondary-500 to-secondary-600 hover:from-secondary-600 hover:to-secondary-700 text-white px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 inline-flex items-center space-x-3"
            >
              <span>Toutes les actualités</span>
              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {news.slice(0, 3).map((article, index) => (
              <article
                key={article.id}
                className="card group cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="relative overflow-hidden">
                  <ImageWithFallback
                    src={article.image}
                    alt={article.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-xs font-semibold rounded-full shadow-lg backdrop-blur-sm">
                      {article.category}
                    </span>
                  </div>
                  
                  {/* Date badge */}
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg">
                    <div className="text-center">
                      <div className="text-sm font-bold text-gray-900">
                        {new Date(article.date).getDate()}
                      </div>
                      <div className="text-xs text-gray-600 uppercase">
                        {new Date(article.date).toLocaleDateString('fr-FR', { month: 'short' })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-6">
                  <div className="text-sm text-gray-500 mb-3 font-medium">
                    {new Date(article.date).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-300 leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 line-clamp-3 leading-relaxed">
                    {article.excerpt}
                  </p>
                  
                  {/* Read more indicator */}
                  <div className="mt-4 flex items-center text-primary-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Lire la suite <FaArrowRight className="ml-2 text-xs" />
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-12 md:hidden">
            <Link 
              to="/actualites" 
              className="btn-outline inline-flex items-center space-x-2"
            >
              <span>Voir toutes les actualités</span>
              <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 via-primary-500 to-accent-600 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0">
          <div className={`absolute top-0 left-0 w-full h-full bg-[url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] opacity-50`} />
          <div className="absolute top-20 right-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-accent-300/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-8 border border-white/30">
              <span className="text-3xl">🤝</span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight">
              Rejoignez notre communauté
              <div className="w-32 h-1 bg-white/80 rounded-full mx-auto mt-6"></div>
            </h2>
            
            <p className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed font-light max-w-3xl mx-auto">
              Ensemble, construisons un avenir durable pour l'agriculture de Saint-Louis. 
              Contactez-nous pour découvrir comment devenir membre de notre coopérative et bénéficier 
              de notre accompagnement technique et commercial.
            </p>
            
            {/* Key benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-3xl mx-auto">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl">🌱</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Formation continue</h3>
                <p className="text-white/80 text-sm">Accès aux dernières techniques agricoles</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl">💼</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Support technique</h3>
                <p className="text-white/80 text-sm">Accompagnement personnalisé</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl">🛒</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Accès au marché</h3>
                <p className="text-white/80 text-sm">Circuits de distribution élargis</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-center gap-6">
              <Link 
                to="/contact" 
                className="group bg-white text-primary-600 hover:bg-gray-100 px-10 py-5 rounded-full text-lg font-bold shadow-2xl hover:shadow-white/20 transition-all duration-300 hover:scale-105 inline-flex items-center justify-center space-x-3"
              >
                <span>Contactez-nous maintenant</span>
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link 
                to="/qui-sommes-nous" 
                className="group bg-transparent border-2 border-white/60 text-white hover:bg-white/10 hover:border-white px-10 py-5 rounded-full text-lg font-bold backdrop-blur-sm transition-all duration-300 hover:scale-105 inline-flex items-center justify-center space-x-3"
              >
                <span>Découvrir la coopérative</span>
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home 